import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Event participant from event_participants table
 */
export interface EventParticipant {
  id: string;
  event_id: string;
  contact_id: string | null;
  email: string;
  display_name: string | null;
  response_status: 'yes' | 'no' | 'maybe' | 'noreply';
  is_organizer: boolean;
  attendance_type: 'required' | 'optional' | 'resource';
  external_id: string | null;
  external_provider: string | null;
  responded_at: string | null;
  created_at: string;
  updated_at: string;
  // Joined data
  contact?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
}

/**
 * Hook to fetch external participants for an event
 * These are participants from the event_participants table (contacts and external emails)
 *
 * @param eventId - The event ID to fetch participants for
 */
const useEventParticipants = (eventId?: string) => {
  return useQuery({
    queryKey: ['event-participants', eventId],
    queryFn: async (): Promise<EventParticipant[]> => {
      if (!eventId) return [];

      const { data, error } = await supabase
        .from('event_participants')
        .select(`
          *,
          contact:contacts(id, first_name, last_name, email)
        `)
        .eq('event_id', eventId)
        .order('is_organizer', { ascending: false })
        .order('created_at', { ascending: true });

      if (error) {
        console.error('[useEventParticipants] Error:', error);
        throw new Error(error.message);
      }

      return data || [];
    },
    enabled: !!eventId,
  });
};

/**
 * Hook to fetch all participants for an event (internal users + external participants)
 * Combines data from event_users and event_participants tables
 *
 * @param eventId - The event ID to fetch all participants for
 */
const useAllEventParticipants = (eventId?: string) => {
  const { data: externalParticipants = [], isLoading: isLoadingExternal } = useEventParticipants(eventId);

  return useQuery({
    queryKey: ['all-event-participants', eventId],
    queryFn: async () => {
      if (!eventId) return { internal: [], external: [] };

      // Fetch internal users
      const { data: internalUsers, error: internalError } = await supabase
        .from('event_users')
        .select(`
          *,
          user:profiles(id, first_name, last_name, email)
        `)
        .eq('event_id', eventId)
        .order('is_organizer', { ascending: false });

      if (internalError) {
        console.error('[useAllEventParticipants] Error fetching internal users:', internalError);
      }

      return {
        internal: internalUsers || [],
        external: externalParticipants,
      };
    },
    enabled: !!eventId && !isLoadingExternal,
  });
};

/**
 * Helper function to get response status badge color
 */
export const getResponseStatusColor = (status: EventParticipant['response_status']): string => {
  switch (status) {
    case 'yes':
      return 'bg-green-100 text-green-800';
    case 'no':
      return 'bg-red-100 text-red-800';
    case 'maybe':
      return 'bg-yellow-100 text-yellow-800';
    case 'noreply':
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

/**
 * Helper function to get response status label
 */
export const getResponseStatusLabel = (status: EventParticipant['response_status']): string => {
  switch (status) {
    case 'yes':
      return 'Accepted';
    case 'no':
      return 'Declined';
    case 'maybe':
      return 'Tentative';
    case 'noreply':
    default:
      return 'No Response';
  }
};
